import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Header from '../component/Header';
import BestandDetails from '../component/BestandDetails';
import FilterComponent from '../component/FilterComponent';
import Pagination from '../component/Pagination';
import NachObenButton from '../component/NachObenButton';
import Footer from '../component/Footer';
import { highlightText, getParam } from '../utils/utils';
import useElementHeight from '../hooks/useElementHeight';
import './VeListe.css';

import { VeListeProps, BeRow, VeListeRow, ListHeader, ClickableButton, FilterButton, ActivableButton } from '../interfaces';
import BackendProvider from '../context/BackendContext';

const listHeaders: ListHeader[] = [
  { id: 1, name: 'vorl. Nr.', field: 'ob_f41' },
  { id: 2, name: 'Titel', field: 'ob_f20' },
  { id: 3, name: 'lfd. Nr.', field: 'ob_f2' },
  { id: 4, name: 'Datierung Findbuch', field: 'ob_f3' }
];

const hitsMessage:string = ' Treffer mit Ihrem Filter finden Sie in der folgenden Tabelle';

function VeListe() {
  const [veList, setVeList] = useState<Array<VeListeRow>>([]);
  const [listEntries, setListEntries] = useState(10);
  const [page, setPage] = useState<number>(1);
  const [sorting, setSorting] = useState({ column: 'ob_f41', ascending: true });
  // const [filterText, setFilterText] = useState('');
  const hits = useRef<number>(0);
  const totalEntries = useRef(0);
  const navigate = useNavigate();
  const filterTextRef = useRef<string>('');
  const divRef = useRef<HTMLDivElement>(null);

  const [urlParams] = useSearchParams();
  const beId = getParam(urlParams, 'beId');
  const beAbk = getParam(urlParams, 'abk');
  const beName = getParam(urlParams, 'name');
  const fId = getParam(urlParams, 'fId');
  const beRow:BeRow = { id: Number(beId), abk: beAbk!, name: beName!, f_id: Number(fId) };

  useElementHeight(divRef, '--ve-liste-header-height');

  useEffect(() => {
    loadList();
  }, [sorting]);

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [page, listEntries]);

  const loadList = useCallback(() => {
    setPage(1);
    let url = '/webclient/php/ve_liste.php?be_id=' + beId + '&sortedBy=' + sorting.column + '&sortingOrder=' + sorting.ascending;
    if (filterTextRef.current !== '') {
      url += '&filterText=' + filterTextRef.current;
    } else {
      url += '&filterText=';
    }

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Anfrage fehlgeschlagen');
        }
        return res.json();
      })
      .then((data) => {
        setVeList(data);
        if (filterTextRef.current !== '') {
          hits.current = data.length;
        } else {
          hits.current = 0;  
        }
        totalEntries.current = data.length;
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, [filterTextRef.current, sorting]);

  const setFilterTextRef = useCallback((text: string) => {
    filterTextRef.current = text;
  }, []);

  function handleClick(id:number) {
    const veUrlParams = new URLSearchParams({
      veId: id.toString(), 
      beId: beId, 
      abk: beAbk, 
      name: beName, 
      fId: fId 
    }).toString(); 
    navigate(`/veformular?${veUrlParams}`);
    // navigate('/veformular', { state: { veId: id, beId: beId, beAbk: beAbk, beName: beName, fId: fId } });
  }

  function sortColumn(column:string) {
    if (column === sorting.column) {
      setSorting({ ...sorting, ascending: !sorting.ascending });
    } else {
      setSorting({ column: column, ascending: true });
    }
  }

  function backToBestandsregie() {
    const newRow = {
      id: -1,
      abk: '',
      name: '',
      f_id: -1
    };
    // setBeRow(newRow);
    // setBestandsregieView('beList');
  }

  function handleHeaderKeyDown(event:React.KeyboardEvent<HTMLDivElement >, column:string) {
    if (event.key === 'Enter' || event.key === ' ') {
      sortColumn(column);
    }
  }

  function handleRowKeyDown(event:React.KeyboardEvent<HTMLDivElement >, id:number) {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(id);
    }
  }

  function filterTextLength(text:string):string {
    if (window.innerWidth < 1200 && text.length > 25) {
      return text.slice(0, 25) + '...';
    }
    if (window.innerWidth > 1199 && text.length > 100) {
      return text.slice(0, 100) + '...';
    }
    return text;
  }

  return (
    <div className="bestandsregie">
      {/* <BackendProvider><Header /></BackendProvider> */}
      <Header />
      <main role="main" className="ve-list" aria-label="Lookup-Liste über alle Verzeichnungseinheiten">
        <div ref={divRef} className="ve-liste-header">
          <nav role="navigation" aria-label="Brotkrumen" className="breadcrumb">
            <ul>
              <li><Link to="/bestandsregie" className="button-as-link" data-text="Bestandsregie">Bestandsregie</Link></li>
              <li aria-current="page">{filterTextLength('Bestand: ' + beAbk + ' - ' + beName)}</li>
            </ul>
          </nav>
          <BestandDetails beRow={beRow} />
          <div role="toolbar" aria-label="Menüleiste" className="toolbar">
            <div className="ve-list-tools">
              <FilterComponent label="VE" loadList={loadList} setFilterTextRef={setFilterTextRef} />
              <div className="ve-list-new">
                <label>VE</label>
                <div>
                  <button onClick={() => handleClick(-1)}>
                    <span>Neue VE anlegen</span>
                    { /* <img src={newFile} alt="" /> */ }
                  </button>
                </div>
              </div>
            </div>
            <Pagination entries={listEntries} setEntries={setListEntries} totalEntries={totalEntries.current} page={page} setPage={setPage} />
          </div>
        </div>
        
        { hits.current > 0 && <h2 className="filter-message">{hits.current + hitsMessage}</h2> }

        <div className="table-wrap">
          {/* 
          <table aria-label="Liste der Verzeichnungseinheiten" className="table ve-list-table">
            <thead>
              <tr>
                {listHeaders.map((row:ListHeader) => (
                  <th 
                    key={row.id} 
                    className={sorting.column === row.field ? (sorting.ascending === true ? 'h2 order-arrow-box asc-order' : 'h2 order-arrow-box desc-order') : 'h2'}
                    title={sorting.column === row.field ? (sorting.ascending === true ? 'Spalteninhalt absteigend sortieren' : 'Spalteninhalt aufsteigend sortieren') : 'Spalteninhalt aufsteigend sortieren'}
                    aria-sort={sorting.column === row.field ? (sorting.ascending ? 'ascending' : 'descending') : 'none'}
                    aria-label={sorting.column === row.field ? (sorting.ascending === true ? 'Spalteninhalt absteigend sortieren' : 'Spalteninhalt aufsteigend sortieren') : 'Spalteninhalt aufsteigend sortieren'}
                    onClick={() => sortColumn(row.field)}
                    onKeyDown={(e) => handleHeaderKeyDown(e, row.field)}
                    tabIndex={0}
                  >
                    {row.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>    
              {veList
                .slice((page - 1) * listEntries, page * listEntries)
                .map((row: VeListeRow) => (
                  <tr 
                    key={row.id}
                    tabIndex={0} 
                    aria-label="Verzeichungseinheit öffnet sich per Klick"
                    onClick={() => handleClick(row.id)} 
                    onKeyDown={(e) => handleRowKeyDown(e, row.id)}
                    title="Verzeichungseinheit öffnet sich per Klick"
                  >
                    <td>{Math.round(row.vnum)}</td>
                    <td>{highlightText(row.titel, filterTextRef.current)}</td>
                    <td>{row.lfd_nr}</td>
                    <td>{highlightText(row.dat_findbuch, filterTextRef.current)}</td>
                  </tr>
                ))}

            </tbody>
          </table>
          */}

          <div role="table" aria-label="Liste der Verzeichnungseinheiten" className="ve-liste-role-table">
            <div className="table-header" role="rowgroup">
              <div role="row">
                {listHeaders.map((row:ListHeader) => (
                  <div 
                    key={row.id} 
                    className={sorting.column === row.field ? (sorting.ascending === true ? 'order-arrow-box asc-order' : 'order-arrow-box desc-order') : ''}
                    role="columnheader"
                    title={sorting.column === row.field ? (sorting.ascending === true ? 'Spalteninhalt absteigend sortieren' : 'Spalteninhalt aufsteigend sortieren') : 'Spalteninhalt aufsteigend sortieren'}
                    aria-sort={sorting.column === row.field ? (sorting.ascending ? 'ascending' : 'descending') : 'none'}
                    aria-label={sorting.column === row.field ? (sorting.ascending === true ? 'Spalteninhalt absteigend sortieren' : 'Spalteninhalt aufsteigend sortieren') : 'Spalteninhalt aufsteigend sortieren'}
                    onClick={() => sortColumn(row.field)}
                    onKeyDown={(e) => handleHeaderKeyDown(e, row.field)}
                    tabIndex={0}
                  >
                    {row.name}
                  </div>
                ))}
              </div>
            </div>
            <div role="rowgroup">    
              {veList
                .slice((page - 1) * listEntries, page * listEntries)
                .map((row: VeListeRow) => (
                  <div 
                    key={row.id}
                    role="row"
                    tabIndex={0} 
                    aria-label="Verzeichungseinheit öffnet sich per Klick"
                    onClick={() => handleClick(row.id)} 
                    onKeyDown={(e) => handleRowKeyDown(e, row.id)}
                    title="Verzeichungseinheit öffnet sich per Klick"
                  >
                    <div role="cell">{Math.round(row.vnum)}</div>
                    <div role="cell">{highlightText(row.titel, filterTextRef.current)}</div>
                    <div role="cell">{row.lfd_nr}</div>
                    <div role="cell">{highlightText(row.dat_findbuch, filterTextRef.current)}</div>
                  </div>
                ))}

            </div>
          </div>

          {veList
            .slice((page - 1) * listEntries, page * listEntries)
            .map((row: VeListeRow) => (
              <div key={row.id} className="table-with-button">
                <table className="table-responsive-md">
                  <tbody>
                    <tr>
                      <th className="h2">{listHeaders[0].name}</th>
                      <td onClick={() => handleClick(row.id)}>{Math.round(row.vnum)}</td>
                    </tr>
                    <tr>
                      <th className="h2">{listHeaders[1].name}</th>
                      <td onClick={() => handleClick(row.id)}>
                        <div className="align-items">
                          {row.titel}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th className="h2">{listHeaders[2].name}</th>
                      <td onClick={() => handleClick(row.id)}>{row.lfd_nr}</td>
                    </tr>
                    <tr>
                      <th className="h2">{listHeaders[3].name}</th>
                      <td onClick={() => handleClick(row.id)}>{row.dat_findbuch}</td>
                    </tr>
                  </tbody>
                </table> 
                <button type="button" onClick={() => handleClick(row.id)} /> 
              </div>
            ))}
        </div>
        <NachObenButton />
        <Footer />
      </main>
    </div> 

  );
}

export default VeListe;
